// F1 Season Processor Module
// Handles season-related processing like summaries and race details

/**
 * Create season summary data with top drivers and race counts
 * @param {Object} data - F1 data object
 * @returns {Object} - Data with season summaries added
 */
export const createSeasonSummaries = (data) => {
  if (!data.races || !data.results || !data.drivers || !data.driver_standings) return data;

  // Create driver name map
  const driverNameMap = {};
  data.drivers.forEach(driver => {
    driverNameMap[driver.driverId] = `${driver.forename} ${driver.surname}`;
  });

  // Group races by year
  const racesByYear = {};
  data.races.forEach(race => {
    const year = race.year;
    if (!racesByYear[year]) {
      racesByYear[year] = [];
    }
    racesByYear[year].push(race);
  });

  // Get final standings for each year
  const standingsByYear = {};
  data.driver_standings.forEach(standing => {
    const race = data.races?.find(r => r.raceId === standing.raceId);
    if (race) {
      const year = race.year;
      if (!standingsByYear[year]) {
        standingsByYear[year] = {};
      }
      // Keep only the highest points for each driver in each year
      const driverId = standing.driverId;
      if (!standingsByYear[year][driverId] || standing.points > standingsByYear[year][driverId].points) {
        standingsByYear[year][driverId] = {
          driverId: driverId,
          driverName: driverNameMap[driverId] || `Driver ${driverId}`,
          points: standing.points || 0,
          wins: standing.wins || 0
        };
      }
    }
  });

  // Get constructor standings for each year
  const constructorStandingsByYear = {};
  if (data.constructor_standings && data.constructors) {
    // Create constructor name map
    const constructorNameMap = {};
    data.constructors.forEach(constructor => {
      constructorNameMap[constructor.constructorId] = constructor.name;
    });

    data.constructor_standings.forEach(standing => {
      const race = data.races?.find(r => r.raceId === standing.raceId);
      if (race) {
        const year = race.year;
        if (!constructorStandingsByYear[year]) {
          constructorStandingsByYear[year] = {};
        }
        // Keep only the highest points for each constructor in each year
        const constructorId = standing.constructorId;
        if (!constructorStandingsByYear[year][constructorId] || standing.points > constructorStandingsByYear[year][constructorId].points) {
          constructorStandingsByYear[year][constructorId] = {
            constructorId: constructorId,
            constructorName: constructorNameMap[constructorId] || `Constructor ${constructorId}`,
            points: standing.points || 0,
            wins: standing.wins || 0
          };
        }
      }
    });
  }

  // Create season summaries
  data.seasonSummaries = Object.keys(racesByYear).map(year => {
    const yearRaces = racesByYear[year];
    const yearStandings = Object.values(standingsByYear[year] || {});
    
    // Sort by points and get top 3
    const topDrivers = yearStandings
      .sort((a, b) => b.points - a.points)
      .slice(0, 3);

    // Get constructor standings for this year
    const yearConstructorStandings = Object.values(constructorStandingsByYear[year] || {});
    
    // Sort all constructors by points
    const allConstructors = yearConstructorStandings
      .sort((a, b) => b.points - a.points);

    return {
      year: parseInt(year),
      raceCount: yearRaces.length,
      topDrivers: topDrivers,
      topDriverNames: topDrivers.map(d => d.driverName).join(', ') || 'No data',
      totalPoints: topDrivers.reduce((sum, d) => sum + d.points, 0),
      allDrivers: yearStandings.sort((a, b) => b.points - a.points),
      allConstructors: allConstructors
    };
  }).sort((a, b) => b.year - a.year);

  return data;
};

/**
 * Create comprehensive season details data with Grand Prix details
 * @param {Object} data - F1 data object
 * @returns {Object} - Data with season race details added
 */
export const createSeasonRaceDetails = (data) => {
  if (!data.races || !data.results || !data.drivers || !data.circuits) return data;

  // Create driver name map
  const driverNameMap = {};
  data.drivers.forEach(driver => {
    driverNameMap[driver.driverId] = `${driver.forename} ${driver.surname}`;
  });

  // Create circuit name map
  const circuitNameMap = {};
  data.circuits.forEach(circuit => {
    circuitNameMap[circuit.circuitId] = circuit.name;
  });

  // Group results by race to get podium and laps info
  const raceDetails = {};
  data.results.forEach(result => {
    const raceId = result.raceId;
    if (!raceDetails[raceId]) {
      raceDetails[raceId] = {
        podium: [],
        laps: {}
      };
    }

    // Add to podium if position 1-3
    if (result.position >= 1 && result.position <= 3) {
      raceDetails[raceId].podium[result.position - 1] = {
        position: result.position,
        driverId: result.driverId,
        driverName: driverNameMap[result.driverId] || `Driver ${result.driverId}`,
        laps: result.laps
      };
    }

    // Track laps for each driver
    raceDetails[raceId].laps[result.driverId] = result.laps;
  });

  // Create enhanced races data for season details view
  data.seasonRaces = data.races.map(race => {
    const details = raceDetails[race.raceId] || { podium: [], laps: {} };
    const podium = details.podium.filter(Boolean);
    
    return {
      ...race,
      circuit: circuitNameMap[race.circuitId] || `Circuit ${race.circuitId}`,
      podium: podium.map(p => p.driverName).join(', ') || 'No podium data',
      podiumDrivers: podium,
      laps: podium.length > 0 ? podium[0].laps : 'Unknown'
    };
  });

  return data;
};

/**
 * Apply all season processing
 * @param {Object} data - F1 data object
 * @returns {Object} - Data with season summaries and race details
 */
export const applySeasonProcessing = (data) => {
  let enhancedData = { ...data };
  
  enhancedData = createSeasonSummaries(enhancedData);
  enhancedData = createSeasonRaceDetails(enhancedData);
  
  return enhancedData;
};
